/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {ClientSideRowModelModule} from '@ag-grid-community/client-side-row-model';
import {ModuleRegistry} from '@ag-grid-community/core';
import {HttpClientModule} from "@angular/common/http";
import "@angular/common/locales/global/it";
import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule} from "@angular/core";
import {initializeApp, provideFirebaseApp} from "@angular/fire/app";
import {MatNativeDateModule} from "@angular/material/core";
import {MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions, MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatPaginatorIntl} from '@angular/material/paginator';
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import {MatToolbarModule} from "@angular/material/toolbar";
import {BrowserModule} from "@angular/platform-browser";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {RouteReuseStrategy} from "@angular/router";
import {ServiceWorkerModule} from "@angular/service-worker";
import {AppComponent} from "@app/app.component";
import {AppRoutingModule} from "@app/app.routing";
import {AppStoreModule} from "@app/store/app-store.module";
import {environment} from "@env/environment";
import {IonicModule, IonicRouteStrategy} from "@ionic/angular";
import {BaseModule} from "@modules/base/base.module";
import {CustomPreferencesService} from "@modules/base/services/custom-preferences.service";
import {StoreTrecTokenRef} from "@modules/base/store-trec-token-ref";
import {C3_MINIO_URL_MISSING_RESOURCE_PLACEHOLDERS_PATH} from "@trec/api/blob-storage";
import {TrecApiModule} from "@trec/api/http";
import {TREC_TOKEN_RENEW_INTERVAL, TrecTokenRef} from "@trec/api/token-ref";
import {
  TrecAuthStoreLocalStorageService,
  TrecAuthStoreService,
  TrecUtilsModule
} from "@trec/api/utils";
import {C3DarkModeService} from "@trec/components/dark-mode";
import {C3GetIconModule} from "@trec/components/get-icon";
import {C3ScrollableModule} from "@trec/components/scrollable";
import {C3SnackbarStatusModule} from "@trec/components/snackbar-status";
import "assets/configs/firebase-config-utils.js"; // Utils
import "assets/configs/firebase-config.js"; // Real Firebase config
import {register} from 'swiper/element/bundle';
import {AppLoadService} from "./services/app.load.service";
import {MatPaginatorIntlIT} from './services/mat-paginator-intl-it';

register();

ModuleRegistry.registerModules([ClientSideRowModelModule]);

function initializeHomeApp(apploadService: AppLoadService) {
  return () => apploadService.initializeHomeApp();
}

const appearance: MatFormFieldDefaultOptions = {
  appearance: 'outline',
  floatLabel: "always"
};

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    // BrowserModule.withServerTransition({appId: 'trec-web-home'}),
    BrowserModule,
    BrowserAnimationsModule,
    IonicModule.forRoot(),
    HttpClientModule, // Used in TrecApiModule
    AppRoutingModule,
    AppStoreModule.forRoot(),
    BaseModule,
    C3ScrollableModule,
    MatSidenavModule,
    MatToolbarModule,
    MatNativeDateModule,
    MatSnackBarModule,
    C3SnackbarStatusModule,
    MatIconModule,

    MatFormFieldModule,

    // Initialize TreC ecosystem
    TrecUtilsModule.forRoot(),
    TrecApiModule.forRoot(),

    // Initialize Service Workers
    ServiceWorkerModule.register(
      environment.production ? "./combined-worker.js" : "./firebase-messaging-sw.js",
      {
        enabled: environment.enableFirebase, // Enable it only when not in dev mode
        // Register it no later than 5sec -> SW must be available when user requests to enable push notification
        registrationStrategy: "registerWhenStable:5000",
      }
    ),

    C3GetIconModule.forRoot({baseUrl: "assets/icons"}),
  ],
  declarations: [
    AppComponent,
  ],
  providers: [

    // Initialize Firebase ecosystem
    provideFirebaseApp(() =>
      initializeApp((window as any).c3GetFirebaseConfigForCurrentEnv().config)
    ),

    // Set the global locale
    {provide: LOCALE_ID, useValue: "it"},

    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},

    {provide: TrecAuthStoreService, useClass: TrecAuthStoreLocalStorageService},
    {provide: TrecTokenRef, useExisting: StoreTrecTokenRef},

    // Styling parameters
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: appearance,
    },

    {
      provide: C3_MINIO_URL_MISSING_RESOURCE_PLACEHOLDERS_PATH,
      useValue: `assets/lib-api/trec-api/blob-storage`,
    },

    CustomPreferencesService, // TODO providedIn: root not works
    AppLoadService,
    {
      // Load services as soon as app bootstraps
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [
        // Load theme configuration at bootstrap (otherwise it is loaded only when a component requries it for the first time)
        C3DarkModeService,
      ],
      multi: true,
    },

    {
      provide: APP_INITIALIZER,
      useFactory: initializeHomeApp,
      deps: [AppLoadService],
      multi: true,
    },

    {provide: TREC_TOKEN_RENEW_INTERVAL, useValue: 14 * 60 * 1000},

    {provide: MatPaginatorIntl, useClass: MatPaginatorIntlIT}
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
